// SPDX-License-Identifier: AGPL-3.0-or-later

import Accessibility from '@app/features/accessibility/state/Accessibility';
import {ConfirmModal} from '@app/features/app/components/dialogs/ConfirmModal';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {modal} from '@app/features/ui/commands/ModalCommands';
import MediaEngine from '@app/features/voice/engine/MediaEngineFacade';
import {useLingui} from '@lingui/react/macro';
import {useCallback, useEffect, useRef, useState} from 'react';

interface PendingVoiceConnectionResult {
	isPending: boolean;
	startConnection: () => void;
	markPending: () => void;
	cancel: () => void;
}
export function usePendingVoiceConnection({
	guildId,
	channelId,
	onConnected,
}: {
	guildId: string | null | undefined;
	channelId: string | null | undefined;
	onConnected?: () => void;
}): PendingVoiceConnectionResult {
	const {t} = useLingui();
	const [isPending, setIsPending] = useState(false);
	const onConnectedRef = useRef(onConnected);
	onConnectedRef.current = onConnected;
	const isConnectedToTarget = channelId
		? MediaEngine.channelId === channelId && MediaEngine.guildId === (guildId ?? null)
		: false;
	const isConnected = MediaEngine.connected;
	const isConnecting = MediaEngine.connecting;
	useEffect(() => {
		if (!isPending) return;
		if (isConnectedToTarget && isConnected) {
			setIsPending(false);
			onConnectedRef.current?.();
			return;
		}
		if (!isConnecting && !isConnectedToTarget) {
			setIsPending(false);
		}
	}, [isPending, isConnectedToTarget, isConnected, isConnecting]);
	const connect = useCallback(() => {
		if (!channelId) return;
		setIsPending(true);
		void MediaEngine.connectToVoiceChannel(guildId ?? null, channelId);
	}, [guildId, channelId]);
	const startConnection = useCallback(() => {
		if (!channelId) return;
		if (guildId && Accessibility.confirmBeforeJoiningVoiceChannels) {
			ModalCommands.push(
				modal(() => (
					<ConfirmModal
						title={t`Join Voice Channel`}
						description={t`Are you sure you want to join this voice channel?`}
						primaryText={t`Join Voice Channel`}
						primaryVariant="primary"
						onPrimary={() => {
							setTimeout(connect, 0);
						}}
					/>
				)),
			);
			return;
		}
		connect();
	}, [channelId, connect, guildId, t]);
	const markPending = useCallback(() => {
		setIsPending(true);
	}, []);
	const cancel = useCallback(() => {
		setIsPending(false);
	}, []);
	return {isPending, startConnection, markPending, cancel};
}
